/**
 * Route guard that gates children behind one or more app roles.
 *
 * The `allow` list is OR-joined via the hierarchy-aware `has()` check,
 * so `allow={["agent"]}` lets managers and admins through too. Use the
 * literal role you want as the *floor*, not the ceiling.
 *
 * On a role mismatch the user is redirected to /forbidden (not /login) —
 * they're authenticated, just not privileged. /forbidden offers a link
 * back to their role-appropriate home.
 */
import { Navigate, Outlet } from "react-router-dom";

import { useRoles, type AppRole } from "@/hooks/auth/useRoles";

interface RequireRoleProps {
  allow: AppRole[];
}

export function RequireRole({ allow }: RequireRoleProps) {
  const { has, isLoading } = useRoles();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <span className="text-sm">Checking permissions…</span>
      </div>
    );
  }

  const ok = allow.some((role) => has(role));
  if (!ok) {
    return <Navigate to="/forbidden" replace />;
  }

  return <Outlet />;
}
